/* ==========================================================================
   LAST PALLET - hero scrub engine

   Desktop: scroll drives currentTime across one pre-rendered clip. Same
   "scroll is a scrubber" idea as the material flight below it, simplified
   to a single clip with no chain and no seams to hold.

   Phones get the content, not the interaction: the pinned scrub track is
   dropped entirely and the same clip plays as a normal muted, looping
   background video. prefers-reduced-motion drops it further still, to the
   poster frame alone, on any viewport.

   Loading strategy depends on how the page is being served:

   - Over http(s): the clip is fetched as a blob and handed to the video
     element as a blob: URL. Seeking into a file that's still progressively
     downloading depends on the host serving byte-range requests, and not
     every host does (confirmed against this project's own dev server:
     it returns the whole file with a 200 on every request, ignoring the
     Range header entirely - a seek past what's already loaded then resets
     to frame 0 instead of landing where requested). Fetching the whole
     clip up front sidesteps that regardless of host, and at a few
     megabytes for a 6 second clip the upfront fetch is cheap.
   - Over file: (the site opened by double-clicking index.html rather than
     being served): fetch() cannot load a file: URL at all - browsers block
     it - so the blob approach fails silently and the hero never advances
     past the poster. Direct src assignment is used instead here, which
     works: a local file doesn't need HTTP range semantics to be seekable,
     the browser can seek it directly on disk.

   Both an MP4 (H.264) and a WebM (VP9) encode are shipped. MP4 is the
   default - it's what iOS Safari requires and what most browsers hardware-
   decode - but some builds (certain Linux/open-source Chromium
   distributions) ship with no H.264 decoder at all. canPlayType picks
   whichever the browser actually supports before loading it, so neither
   format is ever requested and left unplayable.

   Vanilla, no dependencies, no build step.
   ========================================================================== */

(function () {
  'use strict';

  /* A section can legitimately appear more than once on a page, and each
     copy emits its own <script> tag. Bind only once so listeners and
     rAF loops are never doubled up. */
  if (window.__lpHeroScrub) { return; }
  window.__lpHeroScrub = true;

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isCompact() {
    return window.matchMedia('(max-width: 780px)').matches;
  }

  /* MP4/H.264 is the safe default for real-world browsers (iOS Safari
     requires it, most desktop browsers hardware-decode it), but some
     builds ship without it entirely - certain Linux/open-source Chromium
     distributions strip proprietary codecs and only decode royalty-free
     formats. WebM/VP9 is the fallback for exactly that case. Checking
     canPlayType at runtime means both are shipped and the right one loads
     without ever showing a broken video. */
  function pickSource(video, mp4Url, webmUrl, av1Url) {
    // AV1 first: roughly a third the bytes of the H.264 cut at matching
    // quality, and now supported by current Chrome, Edge, Firefox and
    // Safari 17+. Everything below it is the safety net.
    var av1Ok = av1Url && video.canPlayType('video/webm; codecs="av01.0.05M.08"') !== '';
    if (av1Ok) { return av1Url; }
    var mp4Ok = mp4Url && video.canPlayType('video/mp4; codecs="avc1.640028"') !== '';
    if (mp4Ok) { return mp4Url; }
    var webmOk = webmUrl && video.canPlayType('video/webm; codecs="vp9"') !== '';
    if (webmOk) { return webmUrl; }
    return mp4Url || webmUrl || null;
  }

  /* Resolves once `video.src` is set and ready to have loadedmetadata /
     play listeners attached. See the file header for why the strategy
     differs between file: and http(s). */
  function loadVideo(video, url) {
    // Direct src, always. The original blob-fetch existed because the local
    // Python dev server ignored Range headers, so seeking past the buffered
    // point silently reset to frame 0. Shopify's CDN serves proper range
    // requests, so the browser can seek into the clip while it streams -
    // which means the scrub starts working almost immediately instead of
    // waiting for the whole file to download first. On a cold load that is
    // the difference between a hero that responds at once and one that sits
    // on the poster for a second or two.
    video.src = url;
    return Promise.resolve();
  }

  function initHero(root) {
    var track = root.querySelector('.hero__track');
    var stage = root.querySelector('.hero__stage');
    var video = root.querySelector('.hero__video');
    var progressBar = root.querySelector('.hero__progress i');
    if (!track || !stage || !video) { return; }

    var srcDesktop = pickSource(video,
      root.getAttribute('data-src'),
      root.getAttribute('data-src-webm'),
      root.getAttribute('data-src-av1'));
    var srcMobile = pickSource(video,
      root.getAttribute('data-src-mobile'),
      root.getAttribute('data-src-mobile-webm'),
      root.getAttribute('data-src-mobile-av1')) || srcDesktop;
    if (!srcDesktop) { return; }

    var mode = null;        // 'scrub' | 'auto' | 'poster'
    var duration = 0;
    var ready = false;
    var ticking = false;
    var scrolled = false;
    var lastWidth = window.innerWidth;
    var currentBlobUrl = null;
    var introPlaying = false;
    var introRaf = null;
    var introDone = null;
    var introLastY = null;

    function reveal() {
      root.classList.add('is-ready');
    }

    function setupScrub() {
      mode = 'scrub';
      track.style.height = '220vh';
      video.loop = false;
      video.removeAttribute('loop');

      loadVideo(video, srcDesktop).then(function () {
        if (video.src && video.src.indexOf('blob:') === 0) { currentBlobUrl = video.src; }

        video.addEventListener('loadedmetadata', function () {
          duration = video.duration || 6;
          ready = true;
          paint();
        }, { once: true });

        // A muted video that has never played won't paint a seeked frame on
        // iOS Safari. Priming it with a silent play/pause avoids the blank
        // first frame without any visible motion or sound. This resolves
        // before scroll can reach it in practice (metadata alone is a much
        // smaller wait than the paint most users take to start scrolling),
        // but paint() re-checks readiness on every scroll frame regardless.
        var primed = video.play();
        if (primed && primed.then) {
          primed.then(function () { video.pause(); }).catch(function () {});
        }
        video.addEventListener('seeked', reveal, { once: true });
      });

      window.addEventListener('scroll', onScroll, { passive: true });
      paint();
    }

    function setupAutoplay() {
      mode = 'auto';
      track.style.height = '';
      video.loop = true;

      loadVideo(video, srcMobile).then(function () {
        if (video.src && video.src.indexOf('blob:') === 0) { currentBlobUrl = video.src; }
        video.addEventListener('loadedmetadata', function () { ready = true; }, { once: true });
        video.addEventListener('playing', reveal, { once: true });
        var p = video.play();
        if (p && p.catch) { p.catch(function () {}); }
      });
    }

    function paint() {
      ticking = false;
      if (mode !== 'scrub' || !ready) { return; }

      // While the intro is playing itself, the video is the source of truth
      // and drives scroll - not the other way around. Seeking it from here
      // would fight its own playback.
      if (introPlaying) { return; }

      var rect = track.getBoundingClientRect();
      var scrollable = track.offsetHeight - stage.offsetHeight;
      if (scrollable <= 0) { return; }

      var p = clamp(-rect.top / scrollable, 0, 1);
      var t = clamp(p * duration, 0, Math.max(0, duration - 0.08));
      if (Math.abs(video.currentTime - t) > 0.02) {
        try { video.currentTime = t; } catch (err) { /* not seekable yet */ }
      }
      if (progressBar) { progressBar.style.width = (p * 100).toFixed(2) + '%'; }

      if (!scrolled && p > 0.02) {
        scrolled = true;
        root.classList.add('has-scrolled');
      }
    }

    /* ---------------------------------------------------------------
       Self-playing intro.

       Scroll-scrubbing a video is inherently steppy: every scroll update
       asks the decoder for a new random-access frame, and it can only
       land on what it can decode in time. Driving that from a timer to
       auto-advance the page made the jerkiness obvious - the video
       visibly clanked from frame to frame.

       So when the page plays itself, the relationship inverts. The video
       plays natively at its own frame rate (which is what video decoders
       are actually good at) and the *scroll position* is derived from
       currentTime each frame. The motion is as smooth as normal playback
       because it is normal playback, and the page still scrolls in sync
       so the hand-off to manual scrolling is seamless.
       --------------------------------------------------------------- */
    function introFrame() {
      if (!introPlaying) { return; }

      var scrollable = track.offsetHeight - stage.offsetHeight;
      if (scrollable <= 0 || !duration) { stopIntro(); return; }

      // If the page moved to somewhere we didn't put it, something else is
      // driving the scroll - a scrollbar drag, a keyboard page-down, an
      // anchor jump, assistive tech. The input listeners in auto-advance
      // catch the common cases, but not every scroll source fires one, so
      // this is the backstop: whoever else is scrolling wins, immediately.
      if (introLastY !== null && Math.abs(window.scrollY - introLastY) > 4) {
        stopIntro();
        return;
      }

      var trackTop = track.getBoundingClientRect().top + window.scrollY;
      var p = clamp(video.currentTime / duration, 0, 1);
      var targetY = trackTop + scrollable * p;
      window.scrollTo({ top: targetY, left: 0, behavior: 'instant' });
      introLastY = Math.round(window.scrollY);

      if (progressBar) { progressBar.style.width = (p * 100).toFixed(2) + '%'; }
      if (!scrolled && p > 0.02) {
        scrolled = true;
        root.classList.add('has-scrolled');
      }

      if (video.ended || p >= 0.999) { stopIntro(); return; }
      introRaf = window.requestAnimationFrame(introFrame);
    }

    function stopIntro() {
      if (!introPlaying) { return; }
      introPlaying = false;
      introLastY = null;
      if (introRaf) { window.cancelAnimationFrame(introRaf); introRaf = null; }
      try { video.pause(); } catch (err) {}
      if (typeof introDone === 'function') { var cb = introDone; introDone = null; cb(); }
    }

    function playIntro(onDone) {
      if (mode !== 'scrub' || !ready || introPlaying) { return false; }
      // Only from at or near the very top - never yank someone who has
      // already scrolled partway in.
      var scrollable = track.offsetHeight - stage.offsetHeight;
      var rect = track.getBoundingClientRect();
      var p = scrollable > 0 ? clamp(-rect.top / scrollable, 0, 1) : 1;
      if (p > 0.02) { return false; }

      introDone = onDone || null;
      introPlaying = true;
      introLastY = null;
      try { video.currentTime = 0; } catch (err) {}
      var playing = video.play();
      if (playing && playing.catch) {
        playing.catch(function () { introPlaying = false; introDone = null; });
      }
      introRaf = window.requestAnimationFrame(introFrame);
      return true;
    }

    // Exposed so auto-advance.js can hand the intro over to native playback
    // instead of stepping the scrubber. Kept deliberately small.
    root.heroIntro = { play: playIntro, stop: stopIntro,
                       isPlaying: function () { return introPlaying; } };

    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(paint); }
    }

    function teardown() {
      stopIntro();
      window.removeEventListener('scroll', onScroll);
      track.style.height = '';
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (currentBlobUrl) { URL.revokeObjectURL(currentBlobUrl); currentBlobUrl = null; }
      root.classList.remove('is-ready', 'has-scrolled');
      ready = false;
      scrolled = false;
      duration = 0;
    }

    function evaluate() {
      if (prefersReducedMotion()) {
        if (mode !== 'poster') { teardown(); mode = 'poster'; }
        return;
      }
      if (isCompact()) {
        if (mode !== 'auto') { teardown(); setupAutoplay(); }
      } else {
        if (mode !== 'scrub') { teardown(); setupScrub(); }
      }
    }

    // Height-only resizes on touch are the mobile URL bar showing or hiding,
    // not a real layout change. Reacting to them makes a pinned section
    // jump mid-scroll, so only a width change (or, on mobile, orientation)
    // re-evaluates which mode should run.
    window.addEventListener('resize', function () {
      var widthChanged = window.innerWidth !== lastWidth;
      lastWidth = window.innerWidth;
      if (!widthChanged && 'ontouchstart' in window) { return; }
      evaluate();
    }, { passive: true });

    window.addEventListener('orientationchange', function () {
      window.setTimeout(evaluate, 250);
    });

    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.addEventListener) { mq.addEventListener('change', evaluate); }

    evaluate();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-hero-scrub]'), initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
